/**
 * AdRoll Adapter
 * 
 * Adapts event data into AdRoll Pixel format.
 * 
 * Documentation:
 * - https://help.adroll.com/hc/en-us/articles/360040116252-Connect-AdRoll-to-your-site-by-installing-the-AdRoll-Pixel
 * - https://gist.github.com/dennismarlowe (various event examples)
 * 
 * @since 1.51.0
 */

(function (pmw, $, undefined) {

	class AdRollAdapter {
		
		/**
		 * Adapt event data to AdRoll Pixel format
		 * 
		 * @param {object} eventData - Event-specific data (product, order, etc.)
		 * @param {string} eventName - Standardized event name
		 * @param {object} context - Event context (user, page, etc.)
		 * @returns {object|null} Adapted data for AdRoll or null if pixel not loaded
		 */
		adapt(eventData, eventName, context) {
			
			// Check if AdRoll pixel is loaded
			if (!pmwDataLayer?.pixels?.adroll?.loaded) {
				return null;
			}
			
			const eventMap = {
				'search': () => this.adaptProductSearch(eventData),
				'everywhere_else': () => this.adaptHomeView(eventData),
				'view_item': () => this.adaptProductView(eventData),
				'add_to_cart': () => this.adaptAddToCart(eventData),
				'purchase': () => this.adaptPurchase(eventData),
			};
			
			if (!eventMap[eventName]) {
				return null;
			}
			
			return eventMap[eventName]();
		}
		
		/**
		 * Adapt product search event
		 * https://gist.github.com/dennismarlowe/c6826c360d34d9cf5ba7f7011e8a9741#file-product-search
		 */
		adaptProductSearch(eventData) {
			return {
				event_name: 'productSearch',
				event_data: {
					keywords: pmw.getSearchTermFromUrl(),
				}
			};
		}
		
		/**
		 * Adapt home view event
		 * https://gist.github.com/dennismarlowe/59a5e7c7635d54ca36c4c0dd8d34dd70#file-home-view
		 */
		adaptHomeView(eventData) {
			// Only fire on front page
			if (pmwDataLayer?.shop?.page_type !== 'front_page') {
				return null;
			}
			
			return {
				event_name: 'homeView',
				event_data: null
			};
		}
		
		/**
		 * Adapt product view event
		 * https://gist.github.com/dennismarlowe/6ac4fc4b5d0b228f59683135dcdec51b#file-capturing-product-information
		 */
		adaptProductView(eventData) {
			const data = {
				event_name: 'productView',
				event_data: {}
			};
			
			if (eventData?.product?.dyn_r_ids) {
				data.event_data.product_id = eventData.product.dyn_r_ids[pmwDataLayer.pixels.adroll.dynamic_remarketing.id_type];
			}
			
			return data;
		}
		
		/**
		 * Adapt add_to_cart event
		 * https://gist.github.com/dennismarlowe/1fede6dfba0d1243072b32aa23413537#file-add-to-cart
		 */
		adaptAddToCart(eventData) {
			if (!eventData?.product) return null;
			
			const product = eventData.product;
			
			// Ensure dyn_r_ids exists before accessing
			if (!product.dyn_r_ids) return null;
			
			return {
				event_name: 'addToCart',
				event_data: {
					currency: product.currency,
					products: [{
						product_id: product.dyn_r_ids[pmwDataLayer.pixels.adroll.dynamic_remarketing.id_type],
						quantity: product.quantity,
						price: product.price,
					}],
				}
			};
		}
		
		/**
		 * Adapt purchase event
		 * https://gist.github.com/dennismarlowe/090c96c6629d5ca2056a8394cc060c79#file-purchase
		 */
		adaptPurchase(eventData) {
			return {
				event_name: 'purchase',
				event_data: {
					order_id: pmwDataLayer.order.id,
					currency: pmwDataLayer.order.currency,
					conversion_value: pmwDataLayer.order.value.marketing,
					products: pmw.adroll_purchase_items(),
				}
			};
		}
	}
	
	// Register the adapter
	pmw.registerAdapter('adroll', new AdRollAdapter(), {
		supportsS2S: false,
		supportsEventDeduplication: false,
		isActive: () => pmwDataLayer?.pixels?.adroll?.advertiser_id ? true : false
	});
	
}(window.pmw = window.pmw || {}, jQuery));
